import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { authEnabled } from '../auth/auth';
import { useDeveloper } from '../api/DeveloperContext';
import { fetchMyProfile, PROFILE_UPDATED_EVENT, type UserProfile } from '../api/client';
import { fetchAdminAttention } from '../api/developers';
import { getEffectiveTheme, subscribeTheme, toggleTheme, type Theme } from '../lib/theme';

// dev 環境限定の未公開機能フラグ。本番ビルドでは 'false' が渡される。
const shortsEnabled = import.meta.env.VITE_ENABLE_SHORTS !== 'false';

/** ダーク/ライト切り替えボタン。現在の実効テーマ（OS 追随中も含む）に応じたアイコンを出す。 */
function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>(() => getEffectiveTheme());

  useEffect(() => subscribeTheme(setThemeState), []);

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      className="header__theme-btn"
      aria-label={isDark ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
      title={isDark ? 'ライトモードに切り替え' : 'ダークモードに切り替え'}
      onClick={() => setThemeState(toggleTheme())}
    >
      {isDark ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20.7 14.7A8.5 8.5 0 0 1 9.3 3.3a8.5 8.5 0 1 0 11.4 11.4Z"
            fill="currentColor"
          />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="4.5" fill="currentColor" />
          <path
            d="M12 2.5v2.5M12 19v2.5M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2.5 12H5M19 12h2.5M4.2 19.8 6 18M18 6l1.8-1.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

export default function Header() {
  const { user, ready, logout } = useAuth();
  const { state: devState } = useDeveloper();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [attentionTotal, setAttentionTotal] = useState(0);
  const accountRef = useRef<HTMLDivElement>(null);

  // ログイン中はプロフィール（ニックネーム・アイコン）を取得してアバターへ反映する。
  // マイページ等で保存されると PROFILE_UPDATED_EVENT が飛ぶので、そのたびに再取得する。
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    let alive = true;
    const load = () => {
      fetchMyProfile()
        .then((p) => {
          if (alive) setProfile(p);
        })
        .catch(() => {
          // プロフィール未取得でもヘッダーは頭文字フォールバックで表示できる
        });
    };
    load();
    window.addEventListener(PROFILE_UPDATED_EVENT, load);
    return () => {
      alive = false;
      window.removeEventListener(PROFILE_UPDATED_EVENT, load);
    };
  }, [user]);

  // 管理者のときだけ、管理リンクに出す「要対応」バッジ用の合計件数を1回だけ取得する（ポーリングはしない）。
  // 非管理者・モックモードでは API を叩かない。失敗してもバッジを出さないだけに留める。
  useEffect(() => {
    if (!user?.isAdmin) {
      setAttentionTotal(0);
      return;
    }
    let alive = true;
    fetchAdminAttention()
      .then((a) => {
        if (alive) setAttentionTotal(a.openContacts + a.openReports + a.pendingDevelopers);
      })
      .catch(() => {
        // バッジ表示用の補助情報のため、失敗時は非表示のままにする。
      });
    return () => {
      alive = false;
    };
  }, [user?.isAdmin]);

  // メニューの外側クリック / Escape で閉じる。
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const displayName = profile?.nickname || user?.name || '';
  // 開発者リンクは状態確定後に表示する（旧フッターと同じ出し分け）
  const showDevLink = devState.status === 'registered' || devState.status === 'unregistered';
  const showAdminLink = Boolean(user?.isAdmin);

  return (
    <header className="header">
      <Link to="/" className="header__logo">
        VRMA Hub
        <span className="header__alpha">alpha</span>
      </Link>
      <Link to="/about" className="header__about-link">
        About
      </Link>
      {shortsEnabled && (
        <Link to="/shorts" className="header__about-link">
          Shorts
        </Link>
      )}
      <div className="header__spacer" />
      {user && (
        <Link to="/upload" className="btn btn--primary header__upload-btn" aria-label="アップロード">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
          <span className="header__upload-btn-label">アップロード</span>
        </Link>
      )}
      <ThemeToggle />
      {user ? (
        <div className="header__account" ref={accountRef}>
          <button
            type="button"
            className="header__avatar-btn"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="アカウントメニュー"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {profile?.iconUrl ? (
              <img src={profile.iconUrl} alt="" className="header__avatar-img" />
            ) : (
              <span className="header__avatar-fallback" aria-hidden="true">
                {displayName.slice(0, 1).toUpperCase()}
              </span>
            )}
          </button>
          {menuOpen && (
            <div className="header__menu" role="menu">
              <div className="header__menu-user" title={user.email}>
                <div className="header__menu-name">{displayName}</div>
                <div className="header__menu-id">@{user.name}</div>
              </div>
              <div className="header__menu-sep" role="separator" />
              <Link to="/me" className="header__menu-item" role="menuitem" onClick={closeMenu}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M4 20c0-3.3 3.6-5.5 8-5.5s8 2.2 8 5.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                マイページ
              </Link>
              <Link to="/upload" className="header__menu-item" role="menuitem" onClick={closeMenu}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 19V5M5 12l7-7 7 7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                アップロード
              </Link>
              <div className="header__menu-sep" role="separator" />
              <Link
                to="/developers"
                className="header__menu-item"
                role="menuitem"
                onClick={closeMenu}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M6 3h9l4 4v14H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 12h6M9 16h6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                API ドキュメント
              </Link>
              <Link to="/tools/unity" className="header__menu-item" role="menuitem" onClick={closeMenu}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 3 4 7.5 4 16.5 12 21 20 16.5 20 7.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 7.5 12 12 20 7.5M12 12 12 21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Unity ツール
              </Link>
              {showDevLink && (
                <Link
                  to="/developers/portal"
                  className="header__menu-item"
                  role="menuitem"
                  onClick={closeMenu}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="m8 8-4 4 4 4m8-8 4 4-4 4M14 5l-4 14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {devState.status === 'registered' ? '開発者ポータル' : '開発者登録'}
                </Link>
              )}
              {showAdminLink && (
                <Link to="/admin" className="header__menu-item" role="menuitem" onClick={closeMenu}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M12 3 5 6v5c0 4.4 3 8.4 7 9.7 4-1.3 7-5.3 7-9.7V6l-7-3Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="m9.2 12 2 2 3.6-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  管理コンソール
                  {attentionTotal > 0 && (
                    <span className="header__menu-badge" aria-label={`要対応 ${attentionTotal}件`}>
                      {attentionTotal > 99 ? '99+' : attentionTotal}
                    </span>
                  )}
                </Link>
              )}
              <div className="header__menu-sep" role="separator" />
              <a
                href="https://buymeacoffee.com/akunhu"
                target="_blank"
                rel="noopener noreferrer"
                className="header__menu-item"
                role="menuitem"
                onClick={closeMenu}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17 9h1.5a2.5 2.5 0 0 1 0 5H17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 3.5c0 .8-.7 1-.7 1.8s.7 1 .7 1.8M12 3.5c0 .8-.7 1-.7 1.8s.7 1 .7 1.8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                開発を支援する
              </a>
              {authEnabled && (
                <>
                  <div className="header__menu-sep" role="separator" />
                  <button
                    type="button"
                    className="header__menu-item"
                    role="menuitem"
                    onClick={() => {
                      closeMenu();
                      logout();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M15 12H4m0 0 3-3m-3 3 3 3M10 4h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    ログアウト
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        authEnabled &&
        ready && (
          <Link to="/login" className="btn btn--primary">
            ログイン
          </Link>
        )
      )}
    </header>
  );
}
